/**
 * tsParticles preset options + shared override application.
 *
 * Moved from games' packages/shared-socket-events/src/effects/tsParticlesPresets.ts (2026-09-27)
 * (originally apps/electron-client/src/overlays/effects/TsParticlesEffect.tsx).
 *
 * The `any` typing is intentional: tsParticles' own option types are deeply
 * structural and awkward to import into a pure-types package. Runtime shape is
 * validated by tsParticles itself when rendering.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { EffectType, type TsParticlesEffectConfig } from './definitions';

export const CONFETTI_COLORS = [
  '#ff0000', '#00cc00', '#0055ff', '#ffcc00',
  '#ff00cc', '#00ccff', '#ff6600', '#ff69b4',
];

// roll and tilt are intentionally omitted: both manipulate particle color via HSL/backColor
// calculations and produce white particles on a transparent overlay canvas.
export const CONFETTI_TUMBLE = {
  rotate: { value: { min: 0, max: 360 }, direction: 'random' as const, animation: { enable: true, speed: 60, sync: false } },
  wobble: { distance: 30, enable: true, move: true, speed: { min: -15, max: 15 } },
};

export const EFFECT_OPTIONS: Partial<Record<EffectType, Record<string, any>>> = {

  [EffectType.TS_AMBIENT]: {
    particles: {
      number: { value: 60 },
      color: { value: '#aaaaaa' },
      opacity: { value: 0.5 },
      size: { value: { min: 1, max: 3 } },
      move: { enable: true, speed: 0.5, outModes: 'out' },
      links: { enable: true, distance: 150, opacity: 0.3, color: '#ffffff' },
    },
  },

  [EffectType.TS_BIG_CIRCLES]: {
    particles: {
      number: { value: 12 },
      color: { value: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd'] },
      opacity: { value: 0.25 },
      size: { value: { min: 40, max: 100 } },
      move: { enable: true, speed: 0.8, outModes: 'out' },
    },
  },

  [EffectType.TS_BUBBLES]: {
    particles: {
      number: { value: 35 },
      color: { value: '#88ccff' },
      opacity: { value: 0.3 },
      size: { value: { min: 5, max: 22 } },
      move: { enable: true, speed: 2, direction: 'top', outModes: 'out' },
    },
  },

  [EffectType.TS_CONFETTI]: {
    particles: {
      number: { value: 0 },
      color: { value: CONFETTI_COLORS },
      opacity: { value: 1 },
      size: { value: { min: 5, max: 10 } },
      shape: { type: ['square', 'circle'] },
      ...CONFETTI_TUMBLE,
      move: {
        enable: true,
        speed: { min: 8, max: 16 },
        direction: 'bottom',
        gravity: { enable: true, acceleration: 9.81 },
        outModes: { default: 'out', top: 'none' },
      },
    },
    emitters: { position: { x: 50, y: -5 }, rate: { quantity: 20, delay: 0.1 } },
  },

  [EffectType.TS_CONFETTI_CANNON]: {
    particles: {
      number: { value: 0 },
      color: { value: CONFETTI_COLORS },
      opacity: { value: 1 },
      size: { value: { min: 4, max: 9 } },
      shape: { type: ['square', 'circle'] },
      ...CONFETTI_TUMBLE,
      move: {
        enable: true,
        speed: { min: 18, max: 28 },
        gravity: { enable: true, acceleration: 9.81 },
        outModes: { default: 'out', top: 'none' },
      },
    },
    emitters: [
      { position: { x: 0, y: 65 }, particles: { move: { direction: 'top-right' } }, rate: { quantity: 15, delay: 0.15 }, life: { count: 4 } },
      { position: { x: 100, y: 65 }, particles: { move: { direction: 'top-left' } }, rate: { quantity: 15, delay: 0.15 }, life: { count: 4 } },
    ],
  },

  [EffectType.TS_CONFETTI_EXPLOSIONS]: {
    particles: {
      number: { value: 0 },
      color: { value: CONFETTI_COLORS },
      opacity: { value: 1 },
      size: { value: { min: 4, max: 9 } },
      shape: { type: ['square', 'circle', 'triangle'] },
      ...CONFETTI_TUMBLE,
      move: {
        enable: true,
        speed: { min: 12, max: 25 },
        direction: 'none',
        random: true,
        gravity: { enable: true, acceleration: 6 },
        outModes: { default: 'out', top: 'none' },
      },
    },
    emitters: [
      { position: { x: 25, y: 40 }, rate: { quantity: 30, delay: 0.3 }, life: { count: 4 } },
      { position: { x: 75, y: 40 }, rate: { quantity: 30, delay: 0.3 }, life: { count: 4 } },
      { position: { x: 50, y: 60 }, rate: { quantity: 30, delay: 0.3 }, life: { count: 4 } },
    ],
  },

  [EffectType.TS_CONFETTI_FALLING]: {
    particles: {
      number: { value: 200, density: { enable: true } },
      color: { value: CONFETTI_COLORS },
      opacity: { value: 1 },
      size: { value: { min: 3, max: 5 } },
      shape: { type: ['square', 'circle'] },
      ...CONFETTI_TUMBLE,
      move: { enable: true, speed: 5, direction: 'bottom', outModes: 'out' },
    },
  },

  [EffectType.TS_CONFETTI_PARADE]: {
    particles: {
      number: { value: 0 },
      color: { value: CONFETTI_COLORS },
      opacity: { value: 1 },
      size: { value: { min: 5, max: 10 } },
      shape: { type: ['square', 'circle'] },
      ...CONFETTI_TUMBLE,
      move: {
        enable: true,
        speed: { min: 6, max: 10 },
        direction: 'right',
        gravity: { enable: true, acceleration: 2 },
        outModes: { default: 'out', left: 'none' },
      },
    },
    emitters: { position: { x: -5, y: 50 }, size: { width: 0, height: 60 }, rate: { quantity: 6, delay: 0.1 } },
  },

  [EffectType.TS_FIRE]: {
    particles: {
      number: { value: 0 },
      color: { value: ['#ff4400', '#ff7700', '#ffaa00', '#ffcc00', '#ff2200'] },
      opacity: { value: { min: 0.3, max: 0.8 }, animation: { enable: true, speed: 2, sync: false, destroy: 'min' } },
      size: { value: { min: 5, max: 18 }, animation: { enable: true, speed: 5, sync: false, destroy: 'min' } },
      move: {
        enable: true,
        speed: { min: 3, max: 7 },
        direction: 'top',
        random: true,
        outModes: { default: 'out', bottom: 'none' },
      },
      life: { duration: { sync: false, value: { min: 1, max: 2 } }, count: 1 },
    },
    emitters: { position: { x: 50, y: 100 }, size: { width: 80, height: 0 }, rate: { quantity: 8, delay: 0.05 } },
  },

  [EffectType.TS_FIREFLY]: {
    particles: {
      number: { value: 50 },
      color: { value: ['#aaff44', '#88ff22', '#ccff66'] },
      opacity: { value: { min: 0.1, max: 0.8 }, animation: { enable: true, speed: 1, sync: false } },
      size: { value: { min: 1, max: 4 } },
      move: { enable: true, speed: { min: 0.5, max: 1.5 }, random: true, outModes: 'bounce' },
    },
  },

  [EffectType.TS_FIREWORKS]: {
    particles: {
      number: { value: 0 },
      color: { value: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffffff'] },
      opacity: { value: { min: 0.3, max: 1 }, animation: { enable: true, speed: 2, sync: false, destroy: 'min' } },
      size: { value: { min: 2, max: 5 } },
      move: {
        enable: true,
        speed: { min: 10, max: 20 },
        direction: 'none',
        random: true,
        gravity: { enable: true, acceleration: 5 },
        decay: 0.05,
        outModes: { default: 'destroy' },
      },
      life: { duration: { sync: false, value: { min: 0.5, max: 1.5 } }, count: 1 },
    },
    emitters: [
      { position: { x: 30, y: 30 }, rate: { quantity: 40, delay: 1.2 }, life: { count: 3 } },
      { position: { x: 70, y: 25 }, rate: { quantity: 40, delay: 1.5 }, life: { count: 3 } },
      { position: { x: 50, y: 20 }, rate: { quantity: 40, delay: 1.8 }, life: { count: 3 } },
    ],
  },

  [EffectType.TS_FOUNTAIN]: {
    particles: {
      number: { value: 0 },
      color: { value: ['#00aaff', '#0088ff', '#44ccff', '#88eeff'] },
      opacity: { value: 0.7 },
      size: { value: { min: 2, max: 6 } },
      move: {
        enable: true,
        speed: { min: 10, max: 18 },
        direction: 'top',
        gravity: { enable: true, acceleration: 12 },
        outModes: { default: 'destroy', top: 'none' },
      },
      life: { duration: { sync: false, value: { min: 0.8, max: 1.5 } }, count: 1 },
    },
    emitters: { position: { x: 50, y: 100 }, size: { width: 10, height: 0 }, rate: { quantity: 6, delay: 0.05 } },
  },

  // Emitter at center, particles fly outward and decay — matches real hyperspace preset behavior on transparent overlay
  [EffectType.TS_HYPERSPACE]: {
    particles: {
      number: { value: 0 },
      color: { value: '#ffffff' },
      opacity: { value: 1 },
      size: { value: 3 },
      effect: {
        type: 'trail',
        options: { trail: { length: 15, minWidth: 1, maxWidth: 3 } },
      },
      move: {
        enable: true,
        speed: 10,
        decay: 0.005,
        direction: 'outside',
        straight: true,
        outModes: 'destroy',
      },
      life: { count: 1, duration: { value: 5 } },
    },
    emitters: { position: { x: 50, y: 50 }, size: { width: 100, height: 100 }, rate: { quantity: 10, delay: 0.1 }, life: { duration: { value: 3 } } },
  },

  [EffectType.TS_LINKS]: {
    particles: {
      number: { value: 80 },
      color: { value: '#ffffff' },
      opacity: { value: 0.5 },
      size: { value: { min: 1, max: 3 } },
      move: { enable: true, speed: 1.5, outModes: 'out' },
      links: { enable: true, distance: 120, color: '#ffffff', opacity: 0.4, width: 1 },
    },
  },

  [EffectType.TS_MATRIX]: {
    particles: {
      number: { value: 0 },
      color: { value: ['#00ff41', '#008f11', '#00cc33'] },
      opacity: { value: { min: 0.5, max: 1 } },
      size: { value: 8 },
      shape: { type: 'char', options: { char: { value: ['0', '1', 'ﾊ', 'ﾐ', 'ﾋ', 'ｹ', 'ﾒ', 'ｳ', 'ｦ', 'ﾘ'], font: 'Courier New', style: '', weight: '400', fill: true } } },
      move: {
        enable: true,
        speed: { min: 5, max: 12 },
        direction: 'bottom',
        outModes: { default: 'out', top: 'none' },
      },
    },
    emitters: { position: { x: 50, y: -5 }, size: { width: 100, height: 0 }, rate: { quantity: 4, delay: 0.1 } },
  },

  [EffectType.TS_METEORS]: {
    particles: {
      number: { value: 0 },
      color: { value: ['#ffffff', '#aaaaff', '#ffaaaa'] },
      opacity: { value: { min: 0.5, max: 1 } },
      size: { value: { min: 1, max: 3 } },
      effect: {
        type: 'trail',
        options: { trail: { length: 20, minWidth: 1, maxWidth: 2 } },
      },
      move: {
        enable: true,
        speed: { min: 25, max: 50 },
        direction: 'bottom-right',
        outModes: { default: 'out' },
        straight: true,
      },
    },
    emitters: { position: { x: 0, y: 0 }, size: { width: 40, height: 40 }, rate: { quantity: 3, delay: 0.2 } },
  },

  [EffectType.TS_PARTY]: {
    particles: {
      number: { value: 0 },
      color: { value: ['#ff0000', '#ff7700', '#ffff00', '#00ff00', '#0000ff', '#8800ff', '#ff00ff'] },
      opacity: { value: 1 },
      size: { value: { min: 4, max: 12 } },
      shape: { type: ['square', 'circle', 'triangle', 'star'] },
      rotate: { value: { min: 0, max: 360 }, direction: 'random' as const, animation: { enable: true, speed: 25, sync: false } },
      wobble: { distance: 20, enable: true, move: true, speed: { min: -10, max: 10 } },
      move: {
        enable: true,
        speed: { min: 8, max: 20 },
        direction: 'none',
        random: true,
        gravity: { enable: true, acceleration: 5 },
        outModes: { default: 'out' },
      },
    },
    emitters: { position: { x: 50, y: 30 }, rate: { quantity: 12, delay: 0.1 } },
  },

  [EffectType.TS_SEA_ANEMONE]: {
    particles: {
      number: { value: 80 },
      color: { value: ['#ff6b9d', '#c44dff', '#4dafff', '#4dffb0'] },
      opacity: { value: 0.6 },
      size: { value: { min: 2, max: 6 } },
      move: {
        enable: true,
        speed: 1.5,
        random: true,
        outModes: 'bounce',
        warp: true,
      },
      links: { enable: true, distance: 100, opacity: 0.3 },
    },
  },

  [EffectType.TS_SNOW]: {
    particles: {
      number: { value: 200 },
      color: { value: '#ffffff' },
      opacity: { value: { min: 0.4, max: 0.9 } },
      size: { value: { min: 2, max: 6 } },
      move: {
        enable: true,
        speed: { min: 1, max: 3 },
        direction: 'bottom',
        random: true,
        outModes: { default: 'out', top: 'none' },
      },
      wobble: { distance: 10, enable: true, speed: { min: -3, max: 3 } },
    },
  },

  [EffectType.TS_SQUARES]: {
    particles: {
      number: { value: 50 },
      color: { value: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7'] },
      opacity: { value: 0.6 },
      size: { value: { min: 5, max: 15 } },
      shape: { type: 'square' },
      rotate: { value: { min: 0, max: 360 }, animation: { enable: true, speed: 5 } },
      move: { enable: true, speed: 1.5, random: true, outModes: 'out' },
    },
  },

  [EffectType.TS_STARS]: {
    particles: {
      number: { value: 80 },
      color: { value: '#ffffff' },
      opacity: { value: { min: 0.1, max: 1 }, animation: { enable: true, speed: 1.5, sync: false } },
      size: { value: { min: 1, max: 4 } },
      shape: { type: 'star' },
      move: { enable: true, speed: 0.3, random: true, outModes: 'out' },
    },
  },

  [EffectType.TS_TRIANGLES]: {
    particles: {
      number: { value: 50 },
      color: { value: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd'] },
      opacity: { value: 0.6 },
      size: { value: { min: 6, max: 16 } },
      shape: { type: 'triangle' },
      rotate: { value: { min: 0, max: 360 }, animation: { enable: true, speed: 8 } },
      move: { enable: true, speed: 1.5, random: true, outModes: 'out' },
    },
  },
};

/**
 * Merges the optional user overrides from a TsParticlesEffectConfig on top of
 * the preset's `particles` block. Returns only the override object (not merged
 * with base) so callers can spread it under `particles`.
 */
export function buildTsParticlesOverrides(
  baseOptions: Record<string, any>,
  config: TsParticlesEffectConfig,
): Record<string, unknown> {
  const overrides: Record<string, unknown> = {};
  if (config.speed !== undefined)         overrides['move']    = { ...(baseOptions.particles?.move ?? {}), speed: config.speed };
  if (config.opacity !== undefined)       overrides['opacity'] = { value: config.opacity };
  if (config.size !== undefined)          overrides['size']    = { value: config.size };
  if (config.particleCount !== undefined) overrides['number']  = { value: config.particleCount };
  if (config.colors?.length)              overrides['color']   = { value: config.colors };
  return overrides;
}
/* eslint-enable @typescript-eslint/no-explicit-any */
